import { Button } from '@kerfjs/ui/button';
import { Checkbox } from '@kerfjs/ui/checkbox';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { TextField } from '@kerfjs/ui/text-field';
import { attr, type SafeHtml } from 'kerfjs';

import type { SiteRow } from './sites-view.js';

export const SITE_DIALOG_ACTIONS = {
  dialog: attr('data-action', 'site-dialog'),
  cancel: attr('data-action', 'site-cancel'),
  save: attr('data-action', 'site-save'),
} as const;

/**
 * Add or edit one organization site: its name, an optional location, and whether
 * it is the default site for new demands (HS2-DE4RP1). Rendered open by the Sites
 * page while `siteDialog` is set; a failed save shows its message in the dialog.
 */
export function SiteDialog({ site, error }: { site: SiteRow | undefined; error: string }): SafeHtml {
  return (
    <DialogSurface
      label={site === undefined ? 'New Site' : 'Edit Site'}
      lightDismiss
      className="site-dialog"
      rootAttributes={{
        'data-unsaved-guard': 'this site',
        'data-action': SITE_DIALOG_ACTIONS.dialog.value,
        'data-auto-open-dialog': '',
      }}
      footer={
        <>
          <Button
            size="default"
            tone="neutral"
            emphasis="plain"
            action={SITE_DIALOG_ACTIONS.cancel.value}
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            action={SITE_DIALOG_ACTIONS.save.value}
            rootAttributes={{ 'data-unsaved-save': '' }}
          >
            {site === undefined ? 'Add site' : 'Save'}
          </Button>
        </>
      }
    >
      <List gap="m">
        {error === '' ? null : <StateBanner tone="danger" title={error} />}
        <ListInsetControl>
          <TextField name="site-name" label="Name" required maxLength={200} autofocus value={site?.name ?? ''} />
        </ListInsetControl>
        <ListInsetControl>
          <TextField
            name="site-location"
            label="Location"
            hint="City or address, optional"
            maxLength={200}
            value={site?.location ?? ''}
          />
        </ListInsetControl>
        <ListInsetControl>
          <Checkbox name="site-default" checked={site?.isDefault === true} label="Default site for new demands" />
        </ListInsetControl>
      </List>
    </DialogSurface>
  );
}
